// Typed wrappers around `/api/zones/*` and `/api/staff/zones/*`.
//
// Mirrors shared/src/api/exhibits.ts (PR 10 ST 5 / PR 11 ST 10). Built for
// PR 12 ST 4+ consumers: staff/src/features/zones/ ZoneForm + ZoneDashboard
// orchestrators, plus visitor/src/features/zone-detail/ in ST 13.

import { getApiClient } from "./client";
import type { ListStatus } from "./listStatus";
import type { components } from "./types";

export type Zone = components["schemas"]["ZoneResponse"];
export type ZoneListItem = components["schemas"]["ZoneList"];
export type ZonePublic = components["schemas"]["ZonePublicResponse"];
export type ZoneCreate = components["schemas"]["ZoneCreate"];
export type ZoneUpdate = components["schemas"]["ZoneUpdate"];
export type ZoneTransitionRequest =
  components["schemas"]["ZoneTransitionRequest"];
export type ZoneImageUploadResponse =
  components["schemas"]["ZoneImageUploadResponse"];
export type ZoneQrGenerationResponse =
  components["schemas"]["ZoneQrGenerationResponse"];
export type ZoneCodeRegenerationResponse =
  components["schemas"]["ZoneCodeRegenerationResponse"];
export type ZoneDeleteResponse = components["schemas"]["ZoneDeleteResponse"];
export type MediaItem = components["schemas"]["MediaItem"];
export type AssetKind = components["schemas"]["AssetKind"];
export type SetNarrationRequest = components["schemas"]["SetNarrationRequest"];
export type NarrationPreviewResponse =
  components["schemas"]["NarrationPreviewResponse"];

export async function listZones(status?: ListStatus): Promise<ZoneListItem[]> {
  const { data } = await getApiClient().get<ZoneListItem[]>("/zones", {
    params: status ? { status } : {},
  });
  return data;
}

// Staff edit-view fetch — full schema (ZoneResponse) including TC/EN/SC fields
// and exhibit_ids. Distinct from `getZone()` which is visitor-facing.
export async function getStaffZone(zoneId: string): Promise<Zone> {
  const { data } = await getApiClient().get<Zone>(`/staff/zones/${zoneId}`);
  return data;
}

// Visitor view — hydrated public response with embedded exhibits.
export async function getZone(zoneId: string): Promise<ZonePublic> {
  const { data } = await getApiClient().get<ZonePublic>(`/zones/${zoneId}`);
  return data;
}

export async function getZoneByCode(code: string): Promise<ZonePublic> {
  const { data } = await getApiClient().get<ZonePublic>(
    `/zones/by-code/${code}`,
  );
  return data;
}

export async function createZone(payload: ZoneCreate): Promise<Zone> {
  const { data } = await getApiClient().post<Zone>("/zones", payload);
  return data;
}

export async function updateZone(
  zoneId: string,
  payload: ZoneUpdate,
): Promise<Zone> {
  const { data } = await getApiClient().put<Zone>(`/zones/${zoneId}`, payload);
  return data;
}

export async function transitionZone(
  zoneId: string,
  payload: ZoneTransitionRequest,
): Promise<Zone> {
  const { data } = await getApiClient().patch<Zone>(
    `/zones/${zoneId}/transition`,
    payload,
  );
  return data;
}

export async function deleteZone(zoneId: string): Promise<ZoneDeleteResponse> {
  const { data } = await getApiClient().delete<ZoneDeleteResponse>(
    `/zones/${zoneId}`,
  );
  return data;
}

export async function uploadZoneImage(
  zoneId: string,
  file: File,
): Promise<ZoneImageUploadResponse> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await getApiClient().post<ZoneImageUploadResponse>(
    `/zones/${zoneId}/upload-image`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

// ── F-F media + narration (staff) ──────────────────────────────────────
// Mirrors the exhibit calls (shared/src/api/exhibits.ts). Snake_case wire
// per ADR-0020; `/staff/...` namespace so non-published zones still resolve.

export async function setZoneNarration(
  zoneId: string,
  language: string,
  payload: SetNarrationRequest,
): Promise<Zone> {
  const { data } = await getApiClient().put<Zone>(
    `/staff/zones/${zoneId}/narration/${language}`,
    payload,
  );
  return data;
}

export async function generateZoneNarration(
  zoneId: string,
  language: string,
): Promise<Zone> {
  const { data } = await getApiClient().post<Zone>(
    `/staff/zones/${zoneId}/narration/${language}/generate`,
  );
  return data;
}

// F-O: generate a DRAFT preview from custom text — no narration slot is
// touched; confirm by passing the returned assetId to setZoneNarration,
// abandon via discardZoneNarrationPreview.
export async function previewZoneNarration(
  zoneId: string,
  language: string,
  text: string,
): Promise<string> {
  const { data } = await getApiClient().post<NarrationPreviewResponse>(
    `/staff/zones/${zoneId}/narration/${language}/preview`,
    { text },
  );
  return data.assetId;
}

export async function discardZoneNarrationPreview(
  zoneId: string,
  assetId: string,
): Promise<void> {
  await getApiClient().delete(
    `/staff/zones/${zoneId}/narration-previews/${assetId}`,
  );
}

export async function uploadZoneNarration(
  zoneId: string,
  language: string,
  file: File,
): Promise<Zone> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await getApiClient().post<Zone>(
    `/staff/zones/${zoneId}/narration/${language}`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function uploadZoneMedia(
  zoneId: string,
  params: {
    file: File;
    kind: AssetKind;
    language?: string | null;
    caption?: string | null;
  },
): Promise<Zone> {
  const form = new FormData();
  form.append("file", params.file);
  form.append("kind", params.kind);
  if (params.language) form.append("language", params.language);
  if (params.caption) form.append("caption", params.caption);
  const { data } = await getApiClient().post<Zone>(
    `/staff/zones/${zoneId}/media/upload`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export async function updateZoneMedia(
  zoneId: string,
  assetId: string,
  payload: { caption: string | null; language: string | null },
): Promise<Zone> {
  const { data } = await getApiClient().patch<Zone>(
    `/staff/zones/${zoneId}/media/${assetId}`,
    payload,
  );
  return data;
}

export async function removeZoneMedia(
  zoneId: string,
  assetId: string,
): Promise<Zone> {
  const { data } = await getApiClient().delete<Zone>(
    `/staff/zones/${zoneId}/media/${assetId}`,
  );
  return data;
}

export async function reorderZoneMedia(
  zoneId: string,
  assetIds: string[],
): Promise<Zone> {
  const { data } = await getApiClient().put<Zone>(
    `/staff/zones/${zoneId}/media/reorder`,
    { assetIds },
  );
  return data;
}

export async function generateZoneQR(
  zoneId: string,
): Promise<ZoneQrGenerationResponse> {
  const { data } = await getApiClient().post<ZoneQrGenerationResponse>(
    `/zones/${zoneId}/generate-qr`,
  );
  return data;
}

export async function regenerateZoneCode(
  zoneId: string,
): Promise<ZoneCodeRegenerationResponse> {
  const { data } = await getApiClient().post<ZoneCodeRegenerationResponse>(
    `/zones/${zoneId}/regenerate-code`,
  );
  return data;
}
